// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import DistributeOrderModal from "@tps/App/Components/Product/DistributeOrderModal";
import { CustomHasProductContext, withProductContext } from "@tps/App/Store/Contexts/CustomProductContext";
import { CustomWarehouseDto } from "@tps/XDL/types";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    // XNG-Change
    showNumberInCart?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    CustomHasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & CustomHasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const currentCartState = getCurrentCartState(state);
    const isCartLoading = currentCartState.isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        // XNG-Changes
        shouldLoadCart: !currentCartState.value && !isCartLoading,
        cartLines: currentCartState.value?.cartLines || [],
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart) as any,
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const CustomProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, properties },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, inventory },
    },
    productContext,
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    // XNG-Change
    showNumberInCart = false,
    shouldLoadCart,
    cartLines,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    // <XNG-Change>
    const [buttonLabel, setButtonLabel] = useState(translate("Add to Cart"));
    const [isOpen, setIsOpen] = useState(false);
    const [availableQtyInCurrentInventory, setAvailableQtyInCurrentInventory] = useState(0);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false, getCartLines: true });
        }
    }, [shouldLoadCart]);

    const getProductAllQtyAddedToCart = () => {
        let qty = 0;
        cartLines
            ?.filter(cartLine => cartLine.productId === productId)
            ?.forEach(line => {
                qty = qty + (line.qtyOrdered || 0);
            });
        return qty;
    };

    useEffect(() => {
        if (showNumberInCart && cartLines && cartLines.length > 0) {
            const allQtyInCart = getProductAllQtyAddedToCart();
            if (allQtyInCart > 0) {
                setButtonLabel(`Add (${allQtyInCart} In Cart)`);
                return;
            }
        }
        setButtonLabel(translate("Add to Cart"));
    }, [cartLines]);
    
    const getProductQtyAddedToCart = () => {
        const matchedCartLine = cartLines?.find(
            cartLine => cartLine.properties.warehouseId === properties.warehouseId && cartLine.productId === productId,
        );
        return matchedCartLine?.qtyOrdered || 0;
    };
    useEffect(() => {
        const warehouseDtos =
            (inventory?.inventoryWarehousesDtos?.find(
                o => o.unitOfMeasure.toLowerCase() === (unitOfMeasure?.toLowerCase() || ""),
            )?.warehouseDtos as CustomWarehouseDto[]) || undefined;

        const warehouse = warehouseDtos?.find(warehouse => warehouse.properties.warehouseId === properties.warehouseId);
        if (cartLines && cartLines.length > 0) {
            const qtyInCart = getProductQtyAddedToCart();
            if (warehouse && qtyInCart > 0) {
                warehouse && setAvailableQtyInCurrentInventory(warehouse.qty - qtyInCart);
                return;
            }
        }
        warehouse && setAvailableQtyInCurrentInventory(warehouse.qty);
    }, [cartLines, properties.warehouseId, showNumberInCart]);
    // </XNG-Change>

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }



    const addToCartClickHandler = async () => {
        onAddToCartClick?.();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
            // XNG-Change
            properties: { warehouseId: properties.warehouseId },
        })) as CartLineModel;

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    const addToCartHandler = () => {
        if (qtyOrdered > availableQtyInCurrentInventory) {
            setIsOpen(true);
        } else {
            addToCartClickHandler();
        }
    };

    return (
        <>
            <Button
                aria-label={showNumberInCart ? buttonLabel : labelOverride ?? translate("Add to Cart")}
                {...styles}
                onClick={addToCartHandler}
                disabled={qtyOrdered <= 0 || isAddToCartButtonDisabled}
                {...otherProps}
            >
                {showNumberInCart ? buttonLabel : labelOverride ?? translate("Add to Cart")}
            </Button>
            {isOpen && <DistributeOrderModal isOpen={isOpen} setIsOpen={setIsOpen} productContext={productContext} />}
        </>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(CustomProductAddToCartButton));
